import { Ruler, FileCheck, Sparkles } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ProcessSection from '../components/ProcessSection';
import ContactSection from '../components/ContactSection';
import { SITE_INFO } from '../data';

export default function AboutUsPage() {
  return (
    <>
      <SEOHead 
        title={`Om Virksomheden - ${SITE_INFO.name}`}
        description="Læs historien om G28 ApS. Faguddannede tømrere og snedkere med fokus på høj faglighed, gennemskuelighed og godt håndværk."
      />

      {/* Hero Header */}
      <section className="bg-stone-900 text-white py-16 sm:py-20 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
              Om G28 ApS
            </h1>
            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
              Grundlagt med et klart mål: At levere kompromisløst tømrer- og snedkerarbejde til boligejere, der sætter pris på kvalitet, holdbarhed og ordentlighed.
            </p>
          </div>
        </div>
      </section>

      {/* Story & Ethos */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            
            <div className="lg:col-span-6 space-y-6 flex flex-col justify-center">
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight">
                HÅNDVÆRK DER HOLDER<br /><span className="block mt-1">I GENERATIONER</span>
              </h2>
              <p className="text-stone-700 text-base leading-relaxed">
                Vi oplevede, at der manglede et håndværksfirma med større gennemsigtighed, tættere dialog og modet til at tænke anderledes.
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900 tracking-tight">
                Derfor skabte vi G28.
              </h2>
              <p className="text-stone-700 text-base leading-relaxed">
                Vores ambition er at skabe særlige boliger med høj kvalitet og gennemtænkte løsninger – også når den rigtige løsning ikke er den mest traditionelle. Vi er ikke bange for at udfordre standardløsninger, når det giver bedre arkitektur, bedre funktionalitet eller et mere personligt resultat.
              </p>
              <p className="text-stone-700 text-base leading-relaxed">
                Vi arbejder tæt sammen med dygtige arkitekter og andre fagfolk, så idéerne fra de første streger på tegningen bliver omsat til solidt håndværk i virkeligheden.
              </p>
              <p className="text-stone-700 text-base leading-relaxed">
                Tilsammen har vi mere end 35 års erfaring fra byggebranchen. Den erfaring bruger vi til at sikre, at æstetik, funktionalitet og håndværk går hånd i hånd – og at hver detalje bliver udført, som den skal.
              </p>
              <p className="text-stone-700 text-base leading-relaxed">
                Hos G28 får du ikke bare en håndværker. Du får en samarbejdspartner, der tager ansvar for helheden og er med hele vejen fra idé til færdigt byggeri.
              </p>
            </div>

            <div className="lg:col-span-6 flex">
              <div className="w-full h-full min-h-[420px] overflow-hidden border border-stone-200 shadow-xl">
                <img
                  src="https://assets.auxodev.dk/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/img-1333.jpeg"
                  alt="G28 firmabil foran byggeprojekt"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <h2 className="font-heading font-extrabold text-3xl text-white">
              Det garanterer vi vores kunder
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-stone-950 p-8 border border-stone-800 space-y-3">
              <div className="text-amber-500 mb-4">
                <Ruler className="w-8 h-8" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white">Milimeterpræcision</h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Vi måler to gange og skærer én gang. Alle samlinger, hjørner og lister afleveres knivskarpt.
              </p>
            </div>

            <div className="bg-stone-950 p-8 border border-stone-800 space-y-3">
              <div className="text-amber-500 mb-4">
                <FileCheck className="w-8 h-8" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white">Gennemskuelige Aftaler</h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Du modtager et udførligt skriftligt tilbud med fast pris og tidsplan uden ubehagelige overraskelser.
              </p>
            </div>

            <div className="bg-stone-950 p-8 border border-stone-800 space-y-3">
              <div className="text-amber-500 mb-4">
                <Sparkles className="w-8 h-8" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white">Oprydning & Respekt</h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Vi behandler din bolig som vores egen. Vi rydder op efter os hver dag og sikrer en god byggeoplevelse.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection />

      <ContactSection />
    </>
  );
}
